import React from 'react';
import { Box, Container, Typography, Paper, Link } from '@mui/material';

const PrivacyPage: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Privacy Policy
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: January 17, 2026
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography paragraph>
            Pasur Scorekeeper ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and share information when you use our mobile application and website.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1. Information We Collect
          </Typography>
          <Typography paragraph>
            We collect the following types of information:
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Account Information:</strong> When you create an account, we collect your email address, display name, and profile photo (if provided).
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Game Data:</strong> We store your game history, scores, multiplayer match records, and gameplay statistics.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Device Identifiers:</strong> We collect device advertising identifiers (IDFA on iOS, GAID on Android) for advertising purposes, subject to your consent where required.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Push Notification Tokens:</strong> If you enable push notifications, we collect your device token to send you game-related notifications.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Usage Analytics:</strong> We collect information about how you use the app, including features accessed, session duration, and app performance data.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Purchase Information:</strong> If you subscribe to our premium service, we receive confirmation of your purchase from the App Store or Google Play. We do not store your payment card details.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            2. How We Use Your Information
          </Typography>
          <Typography paragraph>
            We use the collected information to:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li>Provide, maintain, and improve our service</li>
              <li>Enable multiplayer gameplay and match you with other players</li>
              <li>Send push notifications about your games (when enabled)</li>
              <li>Display personalized or contextual advertisements</li>
              <li>Analyze usage patterns to improve the user experience</li>
              <li>Process and manage your premium subscription</li>
              <li>Communicate with you about service updates</li>
              <li>Ensure the security and integrity of our platform</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            3. Third-Party Services
          </Typography>
          <Typography paragraph>
            We use the following third-party services that may collect and process your data:
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Firebase (Google):</strong> We use Firebase for authentication, database storage, and analytics. Firebase may collect device information, IP addresses, and usage data. See Google's privacy policy at{' '}
            <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">
              policies.google.com/privacy
            </Link>
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Google Sign-In / Apple Sign-In:</strong> If you sign in with Google or Apple, we receive basic profile information (name, email, profile photo) from your chosen provider.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Google AdMob:</strong> We use AdMob to display advertisements. AdMob may collect device identifiers, IP address, and usage data to serve personalized or contextual ads. See Google's advertising privacy policy at{' '}
            <Link href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener">
              policies.google.com/technologies/ads
            </Link>
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Expo Push Notifications:</strong> We use Expo's push notification service to deliver notifications to your device. Expo receives your push token and notification content.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Apple App Store / Google Play Store:</strong> In-app purchases and subscriptions are processed through these platforms according to their respective privacy policies.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            4. Advertising and Tracking
          </Typography>
          <Typography paragraph>
            We display advertisements through Google AdMob to support our free service. These ads may be personalized based on your interests or contextual based on the content you're viewing.
          </Typography>
          <Typography paragraph>
            <strong>iOS Users:</strong> On iOS 14.5 and later, we request your permission through App Tracking Transparency (ATT) before using your IDFA for personalized advertising. You can change this setting anytime in your device's Settings under Privacy &gt; Tracking.
          </Typography>
          <Typography paragraph>
            <strong>Android Users:</strong> You can opt out of personalized ads by going to Settings &gt; Google &gt; Ads and enabling "Opt out of Ads Personalization."
          </Typography>
          <Typography paragraph>
            If you decline tracking or opt out of personalized ads, you will still see advertisements, but they will be less relevant to your interests.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            5. Data Storage and Security
          </Typography>
          <Typography paragraph>
            Your data is stored securely on Google Cloud infrastructure (Firebase) located in the United States. We implement industry-standard security measures including:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li>Encryption of data in transit using HTTPS/TLS</li>
              <li>Secure password hashing through Firebase Authentication</li>
              <li>Access controls and authentication for database operations</li>
              <li>Regular security monitoring and updates</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            6. Data Sharing
          </Typography>
          <Typography paragraph>
            We do not sell your personal information to third parties. We may share information in the following circumstances:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li><strong>With other players:</strong> Your display name and profile photo are visible to other players in multiplayer games.</li>
              <li><strong>With service providers:</strong> We share data with third-party services described above to operate our app.</li>
              <li><strong>For legal reasons:</strong> We may disclose information if required by law or to protect our rights and safety.</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            7. Your Rights
          </Typography>
          <Typography paragraph>
            Depending on your location, you may have the following rights regarding your personal data:
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Access:</strong> You can view your game data and profile information within the app.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Correction:</strong> You can update your display name and profile information in the Settings screen.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Deletion:</strong> You can delete your account at any time from the Settings screen. This will permanently remove your profile, local game history, and personal data. Your multiplayer game history will be preserved for other players, but your name will appear as "Deleted User."
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Opt-out of Tracking:</strong> On iOS, you can deny tracking permission through the ATT prompt or in Settings. On Android, you can opt out of personalized ads in Google Settings.
          </Typography>
          <Typography component="div" sx={{ pl: 2, mb: 2 }}>
            <strong>Data Portability:</strong> Contact us to request a copy of your data.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            8. Data Retention
          </Typography>
          <Typography paragraph>
            We retain your data as follows:
          </Typography>
          <Typography component="div" sx={{ pl: 2 }}>
            <ul>
              <li><strong>Active accounts:</strong> Your data is kept for as long as your account remains active.</li>
              <li><strong>Deleted accounts:</strong> Personal data is removed immediately upon account deletion.</li>
              <li><strong>Analytics data:</strong> Aggregated analytics may be retained according to Firebase defaults (up to 14 months).</li>
              <li><strong>Game history:</strong> Multiplayer game records may be retained for other participants after your account is deleted.</li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            9. Children's Privacy
          </Typography>
          <Typography paragraph>
            Pasur Scorekeeper is intended for users aged 13 and older. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13, we will take steps to delete that information promptly. If you believe a child under 13 has provided us with personal information, please contact us.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            10. International Data Transfers
          </Typography>
          <Typography paragraph>
            Your information may be transferred to and processed in the United States, where our servers are located. By using our service, you consent to this transfer. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            11. Changes to This Privacy Policy
          </Typography>
          <Typography paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy in the app and updating the "Last Updated" date. We encourage you to review this policy periodically.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            12. Contact Us
          </Typography>
          <Typography paragraph>
            If you have any questions about this Privacy Policy or our data practices, please contact us at{' '}
            <Link href="mailto:team@pasurscorekeeper.com">
              team@pasurscorekeeper.com
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPage;
